<template>
  <UlDialog
    v-model="dialog"
    title-left
    :buttons="buttons"
    :loading="!insuranceCompanyList.length"
    :title="!$auth.user.healthInsuranceCompany ? $t('healthInsuranceProviderBenefits.title') : null"
  >
    <template v-if="insuranceCompanyList.length" #title>
      <v-img
        v-if="
          !!$auth.user.healthInsuranceCompany &&
          currentProvider.additionalInfo.slug !== 'samoplatce'
        "
        eager
        :src="
          $asset(`img/logos/healthInsuranceProviders/${currentProvider.additionalInfo.slug}.svg`)
        "
        max-width="128"
        max-height="48"
        width="fit-content"
        contain
      />
    </template>
    <template #text>
      <HealthInsuranceProviderBenefitsDetail v-if="!!$auth.user.healthInsuranceCompany" />
      <HealthInsuranceProviderBenefitsListHolder v-else />
    </template>
    <template v-if="insuranceCompanyList.length && $auth.user.healthInsuranceCompany" #actions>
      <v-row no-gutters class="flex-column align-center grey py-4">
        <div
          v-if="currentProviderBenefitsData?.updateDate"
          class="text-body-small grey-darken-1--text"
        >
          {{
            $t('healthInsuranceProviderBenefits.footer.updated', {
              date: currentProviderBenefitsData.updateDate
            })
          }}
        </div>
        <div
          class="link"
          data-cy="clientZoneDashboardHealthInsuranceBenefitsDialogLink"
          @click="closeAndRedirect"
        >
          {{
            $t(
              `healthInsuranceProviderBenefits.footer.changeCompany${
                hasBenefits ? 'InSettings' : ''
              }`
            )
          }}
        </div>
      </v-row>
    </template>
  </UlDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import currentHealthInsuranceProvider from '../mixins/currentHealthInsuranceProvider';
import breakpoints from '@/mixins/breakpoints';
import checkUserVerification from '@/mixins/checkUserVerification';
import UlDialog from '@/components/Atoms/UlDialog';
import HealthInsuranceProviderBenefitsDetail from './HealthInsuranceProviderBenefitsDetail';
import HealthInsuranceProviderBenefitsListHolder from './HealthInsuranceProviderBenefitsListHolder';
import partnerInfo from '~/mixins/partnerInfo';
import { patchUser } from '@/services/user/userService.js';

export default {
  name: 'HealthInsuranceProviderBenefitsDialog',
  components: {
    HealthInsuranceProviderBenefitsListHolder,
    HealthInsuranceProviderBenefitsDetail,
    UlDialog
  },
  mixins: [currentHealthInsuranceProvider, breakpoints, checkUserVerification, partnerInfo],
  data() {
    return {
      loading: false
    };
  },
  computed: {
    dialog: {
      get() {
        return this.$store.state.profile.dashboard.dialog === 'insurance-benefits';
      },
      set(value) {
        this.$store.commit('profile/dashboard/setDialog', value);
      }
    },
    ...mapGetters({
      selectedInsuranceCompany: 'profile/healthInsuranceProviderBenefit/selectedInsuranceCompany',
      insuranceCompanyList: 'forms/insuranceCompany/insuranceCompanyList'
    }),
    buttons() {
      return !this.$auth.user.healthInsuranceCompany
        ? [
            {
              type: 'sendButton',
              props: {
                loading: this.loading,
                disabled: !this.selectedInsuranceCompany,
                dataCy: 'healthInsuranceProviderBenefitsDialog'
              },
              events: {
                click: () => {
                  this.updateInsuranceCompany();
                }
              },
              text: this.$t('healthInsuranceProviderBenefits.buttonText')
            }
          ]
        : null;
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.gtmPush({ event: 'feature', 'event.detail': 'feature_show' });
        this.sendAnalyticsUl({
          activity: 'open_health_insurance',
          partner_id: this.partnerId,
          partner_name: this.partnerName,
          delaySeconds: 10
        });
      } else {
        this.cancelUlAnalyticsTimeout();
      }
    }
  },
  async created() {
    await this.loadInsuranceCompanyList({ region: this.$domainRegion });
  },
  methods: {
    closeAndRedirect() {
      if (this.xxlOnly) {
        this.$router.push(this.$localePath('accountSettings'), () => {
          this.dialog = false;
        });
        return;
      }

      this.dialog = {
        name: 'profileMobile',
        params: {
          type: 'basic',
          stepProfile: 2,
          documentName: '' //keep empty params here for dialogParams functionality
        }
      };
    },
    async updateInsuranceCompany() {
      const insuranceCompany = this.insuranceCompanyList.find(
        ic => ic.id === this.selectedInsuranceCompany
      );
      this.loading = true;
      if (!(await this.checkUserVerification(false, true))) {
        this.loading = false;
        return;
      }

      try {
        await patchUser(this.$http, this.$auth, {
          healthInsuranceProviderId: this.selectedInsuranceCompany
        });

        this.gtmPush({
          event: 'feature',
          'event.detail': 'insurance_select',
          'insurance.type': `${insuranceCompany.additionalInfo.countryCode.toLowerCase()}_${
            insuranceCompany.additionalInfo.slug
          }`
        });
      } catch (err) {
        this.handleError({ error: err, code: '0x00000020' });
      } finally {
        this.loading = false;
      }
    },
    ...mapActions({
      loadInsuranceCompanyList: 'forms/insuranceCompany/loadInsuranceCompanyList'
    })
  }
};
</script>
